import React, { useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import axios from "axios";
import customLoginResponse from "../../apis/login/customLogin";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PhoneInput, { isPossiblePhoneNumber, parsePhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import validateEmail from '../../utilities/validateEmail';

const LoginForm = ({ onForgotPassword, onPasswordChangeRequired, pareentCallback, isInterviewMode = false, onInterviewSubmit, isVET = false }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [isPhoneNumberBlank, setIsPhoneNumberBlank] = useState(true);
  const [phoneError, setPhoneError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailCorrect, setIsEmailCorrect] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const toggleAiScreen = searchParams.get("int") === "ai";
  const shouldShowInterviewPhoneField = isInterviewMode && (toggleAiScreen || isVET);

  const jsonConfig = require("../../Config.json");

  const handlePhoneNumberChange = (val) => {
    if (val) {
      const parsedPhone = parsePhoneNumber(val);
      setPhoneNumber(val);
      setPhoneCode(parsedPhone?.countryCallingCode ? `+${parsedPhone.countryCallingCode}` : "");
      setSelectedCountry(parsedPhone?.country || "");
      setIsPhoneNumberBlank(false);
      setPhoneError(!isPossiblePhoneNumber(val));
      return;
    }

    setPhoneNumber("");
    setPhoneCode("");
    setSelectedCountry("");
    setIsPhoneNumberBlank(true);
    setPhoneError(false);
  };



  const handleSubmit = async (e) => {
    e.preventDefault();

    // Interview mode - validate name and email
    if (isInterviewMode) {
      if (shouldShowInterviewPhoneField && !phoneNumber) {
        NotificationManager.error("Please enter your phone number", "Error", 10000);
        return;
      }

      if (shouldShowInterviewPhoneField && phoneError) {
        NotificationManager.error("Please enter a valid phone number", "Error", 10000);
        return;
      }

      if (isVET) {
        if (!email || !name) {
          NotificationManager.error("Please enter your email, first name, and last name", "Error", 10000);
          return;
        }
      } else {
        if (!email || !name) {
          NotificationManager.error("Please enter your email and name", "Error", 10000);
          return;
        }
      }

      if (onInterviewSubmit) {
        setIsLoading(true);
        if (isVET) {
          await onInterviewSubmit({
            email,
            firstName: name,
            lastName,
            ...(shouldShowInterviewPhoneField
              ? { phoneNumber, countryCallingCode: phoneCode, phoneCode, countryCode: selectedCountry }
              : {})
          });
        } else {
          await onInterviewSubmit({
            email,
            name,
            ...(shouldShowInterviewPhoneField
              ? { phoneNumber, countryCallingCode: phoneCode, phoneCode, countryCode: selectedCountry }
              : {})
          });
        }
        setIsLoading(false);
      }
      return;
    }

    // Regular login mode - validate email and password
    if (!email || !password) {
      NotificationManager.error("Please enter your email and password", "Error", 10000);
      return;
    }

    setIsLoading(true);

    try {
      const payloadData = {
        email: email,
        password: password
      };

      const route = jsonConfig.ip_address;
      let ipAddress = "incognito";

      try {
        const ip_address = await axios.get(route);
        ipAddress = ip_address.data.ip;
      } catch (innerError) {
        ipAddress = "incognito";
      }

      const updatedPayload = {
        ...payloadData,
        ipAddress: ipAddress,
      };

      const customLoginData = await customLoginResponse(updatedPayload);

      if (customLoginData.data.message.includes('Invalid Password')) {
        NotificationManager.error("Incorrect username or password", "Error", 10000);
        return;
      }

      if (customLoginData.data.message.includes("We Couldn’t find a ClearedTalent account associated with this email. Please try again.")) {
        NotificationManager.error("We Couldn't find a ClearedTalent account associated with this email. Please try again", "Error", 10000);
        return;
      }

      if (customLoginData.data.message.includes('Your account is not active')) {
        NotificationManager.error("Your account is not active. Please contact the Administrator", "Error", 10000);
        return;
      }

      if (customLoginData.data.message.includes('Your Recruitment Organisation has been deactivated')) {
        NotificationManager.error("Your Recruitment Organisation has been deactivated. Contact support@clearedtalent.com for further assistance.", "Error", 10000);
        return;
      }

      if (customLoginData.data.data?.IsResetRequired && onPasswordChangeRequired) {
        onPasswordChangeRequired(customLoginData.data.data.id, password);
        return;
      }

      // Successful login - call pareentCallback with token
      const token = customLoginData.data.token;
      const userData = customLoginData.data.data;

      if (token) {
        localStorage.setItem("token", token);
        localStorage.setItem('userID', userData.id);

        if (pareentCallback) {
          pareentCallback(token);
        }

        // Handle navigation
        if (location?.state?.isRedrictedFromIvite) {
          navigate("/invitedtest-landing", {
            state: {
              ...location?.state,
              locationCountry: userData.locationCountry
            }
          });
        } else {
          navigate("/landing");
        }
      }

      return customLoginData.data;

    } catch (error) {
      console.error('Sign in error:', error);
      NotificationManager.error("Unable to login, Please click on 'Forgot Password' or contact ClearedTalent support", "Error", 10000);
    } finally {
      setIsLoading(false);
    }
  };


  const handelEmailChange = (e) => {
    const inputValue = e.target.value;
    let isValidEmail = true;
    if (inputValue) {
      isValidEmail = validateEmail(inputValue);
    }
    const sanitizedValue = inputValue.replace(/\+/g, '');
    isInterviewMode ? setEmail(sanitizedValue) : setEmail(inputValue);
    setIsEmailCorrect(isValidEmail);
  }


  return (
    <form onSubmit={handleSubmit}>
      <div className={`login_wrapper_main ${isInterviewMode ? "mt_100" : ""}`}>
        <div className={`login-box-border login_control reset-password-modern ${!isInterviewMode ? "login_container" : ""} `} >
          <div className='container'>
            <div className='candidate-signin-title'>
              {isInterviewMode ? (
                toggleAiScreen ? (
                  <p className="recruiter_signin_header">Verify Email</p>
                ) : (
                  <p className="recruiter_signin_header">Candidate information</p>
                )
              ) : (
                <p className="recruiter_signin_header">Sign-In</p>
              )
              }


              <p className='reset-subtitle'>
                {isInterviewMode ? (
                  toggleAiScreen ? 'Enter your details accurately — these will appear on your score report.' : ' Enter your details to start the interview'
                ) : 'Enter your credentials to access your account'}
              </p>
            </div>


            {isInterviewMode && !toggleAiScreen &&
              <>
                <div className="form-group-reset " style={{ display: 'flex', gap: '20px' }}  >
                  <div>
                    <label htmlFor="name" className='field_name_reset'>First name</label>
                    <input
                      type="text"
                      id="name"
                      className='password-input-reset'
                      placeholder="First Name"
                      name="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <div className="">
                      <label htmlFor="name" className='field_name_reset'>last name</label>
                      <input
                        type="text"
                        id="name"
                        className='password-input-reset'
                        placeholder="Last Name"
                        name="name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </div>
                {isVET && (
                  <div className="form-group-reset mt_20 login-phone-number-group">
                    <label htmlFor="phone" className='field_name_reset'>Phone Number</label>
                    <PhoneInput
                      id="phone"
                      international
                      className={
                        phoneError
                          ? "login-phone-input-error"
                          : "login-phone-input"
                      }
                      placeholder="Enter Phone Number"
                      value={phoneNumber}
                      onChange={handlePhoneNumberChange}
                    />
                    {phoneError && (
                      <p className="login-phone-error-text">Please enter a valid phone number</p>
                    )}
                  </div>
                )}
              </>
            }


            <div className='reset-password-content'>
              <div className='form-group-reset'>
                <label htmlFor="email" className='field_name_reset'>Email</label>
                <input
                  type="text"
                  id="email"
                  className='password-input-reset'
                  placeholder="Enter Your Email"
                  name="email"
                  value={email}
                  onChange={handelEmailChange}
                  required
                />
                {!isEmailCorrect && (
                  <p className="form-error fs-regular">
                    Please enter a valid email address
                  </p>
                )}
              </div>

              <div>
                {isInterviewMode && toggleAiScreen ? (
                  <>
                    <div className="form-group-reset mt_20">
                      <label htmlFor="name" className='field_name_reset'>Name</label>
                      <input
                        type="text"
                        id="name"
                        className='password-input-reset'
                        placeholder="Enter Your Name"
                        name="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                    </div>
                    {shouldShowInterviewPhoneField && (
                      <div className="form-group-reset mt_20 login-phone-number-group">
                        <label htmlFor="phone" className='field_name_reset'>Phone Number</label>
                        <PhoneInput
                          id="phone"
                          international
                          className={
                            phoneError
                              ? "login-phone-input-error"
                              : "login-phone-input"
                          }
                          placeholder="Enter Phone Number"
                          value={phoneNumber}
                          onChange={handlePhoneNumberChange}
                        />
                        {phoneError && (
                          <p className="login-phone-error-text">Please enter a valid phone number</p>
                        )}
                      </div>
                    )}
                  </>
                ) : toggleAiScreen || !isInterviewMode ? (
                  <>
                    <div className="form-group-reset mt_20">
                      <label htmlFor="password" className='field_name_reset'>Password</label>
                      <div className="password-input-wrapper">
                        <input
                          type={showPassword ? "text" : "password"}
                          className='password-input-reset password-input-no-reveal'
                          placeholder="Enter Your Password"
                          id="password"
                          name="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                        />
                        <button
                          type="button"
                          className="password-eye-btn"
                          onClick={() => setShowPassword((prev) => !prev)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="clear_login_remember_sec mt-10">
                      <label className="fgtext cp" onClick={onForgotPassword}>
                        Forgot password?
                      </label>
                    </div>
                  </>
                )
                  : (
                    <>  </>
                  )
                }
              </div>
              {!toggleAiScreen && isInterviewMode &&
                <></>
                // <div className="form-group-reset " style={{ display: 'flex', gap: '20px' }}  >
                //   <div className="form-group-reset " style={{ width: '50%' }} >
                //     <label htmlFor="name" className='field_name_reset'>First language</label>
                //     <select
                //       id="language"
                //       value={"English"}
                //       // onChange={(e) => setLanguage(e.target.value)}
                //       className="password-input-re"
                //     >
                //       <option value="english">English</option>
                //       <option value="hindi" disabled>
                //         Hindi
                //       </option>
                //     </select>
                //   </div>
                //   <div style={{ width: '50%' }}>
                //     <div className="form-group-re" >
                //       <div>
                //         <label htmlFor="name" className='field_name_reset'>Test purpose</label>
                //         <select
                //           id="language"
                //           value={"Employment screening"}
                //           // onChange={(e) => setLanguage(e.target.value)}
                //           className="password-input-re"
                //         >
                //           <option value="Employment_screening">Employment screening</option>
                //           <option value="academic" disabled>
                //             Academic admission
                //           </option>
                //         </select>
                //       </div>
                //     </div>
                //   </div>
                // </div>
              }

              <div className='reset-password-btn-container'>
                <button
                  className="btn-reset-password-modern full-width"
                  type="submit"
                  disabled={
                    isLoading
                    || !email
                    || (isInterviewMode ? !name : !password)
                    || (shouldShowInterviewPhoneField && (!phoneNumber || phoneError) || !isEmailCorrect)
                  }
                >
                  {isInterviewMode ? (isLoading ? 'Continue...' : 'Continue') : (isLoading ? 'Signing In...' : 'Sign-In')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default LoginForm;
